import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchSearchHistory } from '../api/matchmakingApi';
import { processHistoryItems } from '../utils/history.utils';
import type { HistoryItem } from '../components/MatchmakingHistoryPopover';

type UseMatchmakingHistoryProps = {
  advisorId: string | null;
  clientId: string | null;
  isOpen: boolean;
  shouldFetch?: boolean;
  onFetchComplete?: () => void;
};

export const useMatchmakingHistory = ({
  advisorId,
  clientId,
  isOpen,
  shouldFetch = false,
  onFetchComplete,
}: UseMatchmakingHistoryProps) => {
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const lastFetchKeyRef = useRef<string>('');
  const hasFetchedForCurrentTriggerRef = useRef<boolean>(false);
  const isFetchingRef = useRef<boolean>(false);

  const fetchHistory = useCallback(async () => {
    if (!advisorId || !clientId || isFetchingRef.current) {
      return;
    }

    const fetchKey = `${advisorId}-${clientId}`;
    isFetchingRef.current = true;
    setIsLoading(true);

    try {
      const data = await fetchSearchHistory({ advisorId, clientId });
      const historyArray =
        data.status === 'SUCCESS' && Array.isArray(data.history)
          ? data.history
          : Array.isArray(data)
            ? data
            : Array.isArray(data.history)
              ? data.history
              : [];

      if (historyArray.length > 0) {
        const allHistory = processHistoryItems(historyArray);
        setHistory(allHistory);
        lastFetchKeyRef.current = fetchKey;
      } else {
        setHistory([]);
      }
    } catch (err) {
      console.error('Error fetching history:', err);
      setHistory([]);
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [advisorId, clientId]);

  // Reset state when client/advisor changes (but don't set lastFetchKeyRef yet)
  // The lastFetchKeyRef will be set after successful fetch in fetchHistory function
  useEffect(() => {
    if (clientId && advisorId) {
      const fetchKey = `${advisorId}-${clientId}`;
      if (lastFetchKeyRef.current !== fetchKey) {
        setHistory([]);
        hasFetchedForCurrentTriggerRef.current = false;
        // Don't set lastFetchKeyRef here - it will be set after successful fetch
      }
    }
  }, [clientId, advisorId]);

  const fetchHistoryRef = useRef(fetchHistory);
  useEffect(() => {
    fetchHistoryRef.current = fetchHistory;
  }, [fetchHistory]);

  const onFetchCompleteRef = useRef(onFetchComplete);
  useEffect(() => {
    onFetchCompleteRef.current = onFetchComplete;
  }, [onFetchComplete]);

  useEffect(() => {
    if (isOpen && advisorId && clientId && !isFetchingRef.current) {
      const fetchKey = `${advisorId}-${clientId}`;
      if (lastFetchKeyRef.current !== fetchKey) {
        fetchHistoryRef.current();
      }
    }
  }, [isOpen, advisorId, clientId]);

  useEffect(() => {
    if (
      shouldFetch &&
      advisorId &&
      clientId &&
      !hasFetchedForCurrentTriggerRef.current &&
      !isFetchingRef.current
    ) {
      hasFetchedForCurrentTriggerRef.current = true;
      fetchHistoryRef.current().then(() => {
        if (onFetchCompleteRef.current) {
          onFetchCompleteRef.current();
        }
      });
    }
  }, [shouldFetch, advisorId, clientId]);

  // Fetch history on initial load or when client/advisor changes (even if popover is closed)
  useEffect(() => {
    if (advisorId && clientId && !isFetchingRef.current) {
      const fetchKey = `${advisorId}-${clientId}`;
      // Only fetch if we haven't fetched for this client/advisor combination yet
      if (lastFetchKeyRef.current !== fetchKey) {
        fetchHistoryRef.current();
      }
    }
  }, [advisorId, clientId]);

  useEffect(() => {
    if (!shouldFetch) {
      hasFetchedForCurrentTriggerRef.current = false;
    }
  }, [shouldFetch]);

  return { history, isLoading };
};

